import { computed, DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { select, setProp } from '@ngneat/elf';
import { Apollo, gql } from 'apollo-angular';
import { cloneDeep } from 'lodash-es';
import { combineLatest, map } from 'rxjs';
import { AuthenticationService, AuthorizationService } from '~ngx-shared/authentication';
import { FormlyUtil } from '~ngx-shared/formly';
import { StoreService } from '~ngx-shared/services/store.service';
import { AccessUserModel } from '../models';

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly destroyRef = inject(DestroyRef);
  private apollo = inject(Apollo);
  private storeService = inject(StoreService);
  private authenticationService = inject(AuthenticationService);
  private authorizationService = inject(AuthorizationService);

  userStore = this.storeService.createStore<{ user: AccessUserModel | undefined }>({
    name: 'user',
    initial: undefined,
    persist: { storage: 'session' }
  });

  user$ = this.userStore.pipe(select(state => state.user));

  readonly user = toSignal(this.user$);

  readonly isBirthdayToday = computed(() => {
    const user = this.user();
    if (user) {
      const today = new Date();
      const dateOfBirth = FormlyUtil.fromIsoDateString(
        user.current_person_data?.date_of_birth,
        false
      );
      return (
        today.getDate() === dateOfBirth?.getDate() && today.getMonth() === dateOfBirth?.getMonth()
      );
    }
    return false;
  });

  private _intervalId: any;
  private readonly _readUserIds = new Set<string>();

  init() {
    combineLatest([this.authenticationService.authModel$, this.authorizationService.currentRole$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([authModel, currentRole]) => {
        if (authModel.isLoggedOut) {
          this.logout();
        } else if (
          authModel.isAuthenticated &&
          authModel.roles &&
          authModel.roles.length &&
          authModel.claims?.['userId'] &&
          currentRole &&
          this._needsUser(authModel.claims['userId'])
        ) {
          this._readUserIds.add(authModel.claims['userId']);
          this._loadUser(authModel.claims['userId']);
        }
      });
  }

  // A stored user without a person id is unusable: every query that filters by the person sends null
  private _needsUser(userId: string): boolean {
    const user = this.user();
    if (!user || user.id !== userId) {
      return true;
    }
    if (!user.person_id) {
      return !this._readUserIds.has(userId);
    }
    return false;
  }

  private _loadUser(userId: string, attempt = 0): void {
    this.apollo
      .query<{ result: AccessUserModel }>({
        query: gql`
          query ReadAccessUserById($id: uuid!) {
            result: access_user_by_pk(id: $id) {
              id
              person_id
              email
              current_person_data {
                person_id
                academic_degree_prefix
                academic_degree_suffix
                first_name
                last_name
                date_of_birth
                gender
                can_sign
              }
            }
          }
        `,
        variables: {
          id: userId
        },
        fetchPolicy: 'network-only'
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(queryResult => queryResult.data?.result)
      )
      .subscribe(result => {
        if (result) {
          this.userStore.update(setProp('user', cloneDeep(result)));
          if (!this._intervalId) {
            this._intervalId = setInterval(() => this._updateLastSeen(), 120000);
          }
        } else if (attempt < 2) {
          setTimeout(() => this._loadUser(userId, attempt + 1), 2000);
        }
      });
  }

  logout() {
    this._stopLastSeen();
    // Clear store
    this.userStore.update(setProp('user', undefined));
  }

  private _updateLastSeen(): void {
    this.apollo
      .mutate({
        mutation: gql`
          mutation UpdateUserLastSeen($userId: uuid!) {
            update_access_user_by_pk(_set: { last_seen_at: "now()" }, pk_columns: { id: $userId }) {
              __typename
            }
          }
        `,
        variables: {
          userId: this.userStore.value?.user?.id
        }
      })
      .subscribe();
  }

  private _stopLastSeen(): void {
    if (this._intervalId) {
      clearInterval(this._intervalId);
    }
  }
}
